import { Fragment, useContext, useCallback } from 'react'

import slugify from 'scripts/utils/slugify'

import { CustomHelmet } from 'reactShared/components/CustomHelmet'

import productSchema from '../../utils/productSchema'
import { buildPagination } from '../../utils/buildUrl'
import { EntryKeyContext } from '../../context/EntryKeyContext'
import { DEFAULT_META_TAGS } from '../../utils/constants'

const HeadTags = () => {
  const {
    isSearch,
    query,
    type,
    metaTags,
    metadata,
    isLoadingProducts,
    products,
    pageType,
    baseUrl,
    isMappedSearch,
    categoryId,
    personalizationTrack
  } = useContext(EntryKeyContext)

  const getGaTrackData = (gaTrack) => {
    const content = !isMappedSearch
      ? gaTrack
      : `lista/${gaTrack}`

    const gaTrackData = {
      content: gaTrack ? content : null
    }

    if (pageType) {
      gaTrackData['data-page-type'] = pageType
    }

    return gaTrackData
  }

  const renderProductsJSONLD = useCallback(() => {
    const schemas = products.map(product => productSchema(product))

    return `${JSON.stringify(schemas)}`
  }, [products])

  const componentMetaTags = useCallback(
    value => {
      if (isSearch) {
        return {
          title: `Resultados da Busca por: ${value}`,
          description: `Veja o que a Leroy Merlin encontrou para os termos: ${value}`
        }
      }

      if (type === 'imageSearch') {
        return {
          title: 'Resultados da Busca por Imagem',
          description:
            'Confira alguns produtos que podem ser o que você procura'
        }
      }

      return metaTags
    },
    [isSearch, type, metaTags]
  )

  const getCanonical = canonical => {
    if (!isMappedSearch) return canonical

    const termSlug = slugify(query.term.value)
    const pageQuery = metadata.page > 1 ? `?page=${metadata.page}` : ''

    return `${window.location.origin}/${termSlug}${pageQuery}`
  }

  const productsIds = products.map(product => product.id)

  const {
    title = DEFAULT_META_TAGS.title,
    description = DEFAULT_META_TAGS.description,
    image = DEFAULT_META_TAGS.image
  } = componentMetaTags(query.term?.value)

  const gaTrack = getGaTrackData(metaTags?.gaTrack)
  const canonical = getCanonical(metaTags?.canonical)

  const shouldRenderGaTrack = gaTrack.content || gaTrack.pageType

  const { prevPage, nextPage } = buildPagination({
    query,
    currentPage: metadata?.page,
    pageCount: metadata?.pageCount,
    baseUrl,
    isMappedSearch
  })

  return (
    <Fragment>
      <CustomHelmet>
        <title>{title}</title>

        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={image} />
        {shouldRenderGaTrack && <meta name="ga-track" {...gaTrack} />}

        {canonical && <link rel="canonical" href={canonical} />}

        {nextPage && <link rel="next" href={nextPage} />}
        {prevPage && <link rel="prev" href={prevPage} />}

        {!isLoadingProducts && (
          <script type="application/ld+json">{renderProductsJSONLD()}</script>
        )}
      </CustomHelmet>

      {personalizationTrack && (
        <span
          data-component="rich-relevance/category-tracking"
          data-api-key={personalizationTrack.apiKey}
          data-base-url={personalizationTrack.baseUrl}
          data-region-id={personalizationTrack.regionId}
          data-placement-type={personalizationTrack.placement}
          data-products={JSON.stringify(productsIds)}
          data-category-id={categoryId}
          data-category-name={personalizationTrack.categoryName}
        />
      )}
    </Fragment>
  )
}

export default HeadTags
